<template>
  <div class="step-tab">
    <!-- Step heading (per language). Blank → buyer-facing default. -->
    <div class="cp-card cp-card--heading">
      <CustomInput
          :label="L.stepTitleLabel || 'Step title'"
          :placeholder="L.stepTitlePlaceholder || 'Questions & Remarks'"
          :value="text.checkout_custom_questions_title"
          @input="setText('checkout_custom_questions_title', $event)" />
      <CustomInput
          :label="L.stepSubtitleLabel || 'Step subtitle'"
          :placeholder="L.stepSubtitlePlaceholder || 'Please complete the following.'"
          :value="text.checkout_custom_questions_subtitle"
          @input="setText('checkout_custom_questions_subtitle', $event)" />
    </div>

    <div class="cp-card">
      <div class="cp-questions-head">
        <p class="cp-hint">
          {{ L.questionsHint || 'Custom fields shown to the buyer during checkout. Answers are sent with the reservation, never stored.' }}
        </p>
        <md-button class="md-raised md-primary cp-addbox" @click="$emit('add-box')">
          <md-icon>add</md-icon>{{ L.addBox || 'Add box' }}
        </md-button>
      </div>

      <p v-if="!questions.length" class="cp-empty">{{ L.noBoxes || 'No boxes yet. Add a box, then add elements to it.' }}</p>

      <div class="cp-box-grid">
      <BoxCard
          v-for="(box, bIndex) in questions"
          :key="box.uid"
          :options="options"
          :box="box"
          :index="bIndex"
          :total="questions.length"
          :sibling-names="questions.filter(b => b !== box).map(b => (b.name || '').trim().toLowerCase())"
          :lang="lang"
          @move="(i, dir) => $emit('move-box', i, dir)"
          @remove="i => $emit('remove-box', i)"
          @add-element="(b, t) => $emit('add-element', b, t)"
          @remove-element="(b, i) => $emit('remove-element', b, i)"
          @move-element="(b, i, dir) => $emit('move-element', b, i, dir)"
          @change-element-type="(el, t) => $emit('change-element-type', el, t)"
          @add-option="el => $emit('add-option', el)"
          @remove-option="(el, i) => $emit('remove-option', el, i)" />
      </div>
    </div>

    <PreviewFrame :label="L.preview || 'Preview'">
      <QuestionsPreview :questions="questions" :text="text" :lang="lang" :brandColor="brandColor" />
    </PreviewFrame>
  </div>
</template>

<script>
import BoxCard from './questions/BoxCard.vue'
import CustomInput from '../../parts/CustomInput.vue'
import PreviewFrame from './previews/PreviewFrame.vue'
import QuestionsPreview from './previews/QuestionsPreview.vue'

/**
 * Questions & Remarks step tab: thin shell. Editable step heading (per language) on
 * top, then one BoxCard per box plus a live QuestionsPreview. All box/element CRUD
 * stays owned by the parent editor — this tab only forwards array-level events.
 */
export default {
  name: 'StepTabQuestions',
  components: { BoxCard, CustomInput, PreviewFrame, QuestionsPreview },
  props: {
    options: { type: Object, required: true },
    questions: { type: Array, default: () => [] },   // array of boxes
    text: { type: Object, default: () => ({}) },      // texts[lang] (mutated in place)
    lang: { type: String, default: 'de' },
    brandColor: { type: String, default: '#1a73e8' }
  },
  computed: {
    L() { return this.options.Lang || {}; }
  },
  methods: {
    setText(key, val) { this.$set(this.text, key, val); }
  }
};
</script>

<style scoped>
.step-tab { animation: cp-fade .2s ease; }
@keyframes cp-fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }

.cp-card { background: #fff; border-radius: 1rem; box-shadow: 0 2px 12px rgba(0,0,0,0.06); padding: 1.5rem 1.75rem; }

/* Boxes flow two-per-row where there's space; collapse to one column when narrow. */
/* Center the icon + label inside the Add-box button. */
.cp-addbox >>> .md-button-content { display: inline-flex; align-items: center; justify-content: center; gap: 0.4rem; }
.cp-addbox .md-icon { margin: 0 !important; }

.cp-box-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1.25rem; align-items: start; }
@media (max-width: 900px) { .cp-box-grid { grid-template-columns: 1fr; } }
.cp-card--heading { display: flex; flex-direction: row; gap: 1.25rem; margin-bottom: 1.25rem; flex-wrap: wrap; }
.cp-card--heading > * { flex: 1; min-width: 220px; }
.cp-questions-head { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 1rem; }
.cp-hint { font-size: 0.8rem; color: #888; margin: 0; max-width: 560px; }
.cp-empty { font-style: italic; color: #999; padding: 1rem 0; }
</style>
